import { FileIcon } from '@commenda/icons';
import { FileBasic } from '@commenda/types/src/file/types';
import { cn, downloadFileFromLink } from '@commenda/utils';
import { fileIconMap } from '@commenda/utils/file-icon-map';
import { LegacyDocumentType } from '@prisma/client';
import React, { useCallback, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { useToggle } from '../../hooks';
import { FeaturedIcon, Icons } from '../Icons';
import { LoadingIndicator } from '../LoadingIndicator';
import { PreviewDownloadDialog } from '../PreviewDownloadDialog';
import { uploadFileToS3 } from '../SingleFileUploader/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';

const MultiFileUploader = ({
  files,
  setFiles,
  documentType = LegacyDocumentType.Other,
  onError,
  accept,
  maxSize = Infinity,
  maxFiles,
  _className = '',
  setErrorMessage = () => { },
  onFilesChange,
  onFileUpload,
  disabled = false,
  token,
  companyId,
  message,
  onUploadingChange,
  clerkToken,
  onPreviewFile,
}: {
  files: FileBasic[];
  setFiles?: React.Dispatch<React.SetStateAction<FileBasic[]>>;
  documentType?: LegacyDocumentType;
  onError: () => void;
  accept?: Accept;
  _className?: string;
  maxSize?: number;
  maxFiles?: number;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
  onFilesChange?: (data: FileBasic[]) => void;
  // Nullable to match uploadFileToS3; an override that throws has no handler here.
  onFileUpload?: (
    file: File,
    companyId: number | undefined,
    token: string | undefined
  ) => Promise<{ fileUrl: string; downloadUrl?: string } | null>;
  disabled?: boolean;
  token?: string | undefined;
  companyId: number | undefined;
  message?: string;
  onUploadingChange?: (isUploading: boolean) => void;
  clerkToken?: string;
  /**
   * When provided, the file row's preview (eye) action calls this instead of opening the
   * built-in preview modal — lets the consumer render its own preview (e.g. an inline pane).
   */
  onPreviewFile?: (file: FileBasic) => void;
}) => {
  const [isUploadingFiles, setUploadingFiles] = useState(false);
  const [uploadingFilesCount, setUploadingFilesCount] = useState(0);

  const [previewFile, setPreviewFile] = useState<{
    fileName: string;
    signedURL: string;
  } | null>(null);
  const previewModalVisibility = useToggle();

  const onDrop = useCallback(
    async (acceptedFiles: File[], _rejectedFiles: FileRejection[]) => {
      setUploadingFiles(true);
      onUploadingChange?.(true);
      setUploadingFilesCount(acceptedFiles.length);
      const uploadFilePromises = [];

      const uploadFn = onFileUpload || uploadFileToS3;

      for (let i = 0; i < acceptedFiles.length; i += 1) {
        uploadFilePromises.push(uploadFn(acceptedFiles[i], companyId, token));
      }

      try {
        const uploadFileResults = await Promise.all(uploadFilePromises);

        const uploadedFiles = uploadFileResults.map((file, idx) => ({
          fileName: acceptedFiles[idx]?.name as string,
          fileUrl: file?.fileUrl as string,
          signedUrl: file?.downloadUrl || undefined,
          documentType: Object.values(LegacyDocumentType).includes(documentType)
            ? documentType
            : LegacyDocumentType.Other,
        }));

        if (setFiles) setFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);

        if (onFilesChange) onFilesChange([...(files || []), ...uploadedFiles]);

        setUploadingFiles(false);
        onUploadingChange?.(false);
      } catch (error) {
        console.error(error);
        onError();
        setUploadingFiles(false);
        onUploadingChange?.(false);
      }
    },
    [setFiles, onFilesChange, files, documentType, onError, onUploadingChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    ...(accept ? { accept } : {}),
    ...(maxFiles ? { maxFiles } : {}),
    ...(maxSize ? { maxSize } : {}),
    onError: (err) => setErrorMessage(err.message),
  });

  const handleRemoveFile = (idx: number) => {
    if (setFiles)
      setFiles((prevFiles) => prevFiles.filter((_, index) => index !== idx));

    if (onFilesChange) onFilesChange(files.filter((_, index) => index !== idx));
  };

  // Resolve signedURL for preview/download
  // Priority: 1) file.signedUrl (already available), 2) file.id (fetch via authorized endpoint)
  const resolveSignedUrl = async (file: FileBasic): Promise<string | null> => {
    if (file.signedUrl) return file.signedUrl;

    // If file has an id, fetch signed URL via authorized endpoint
    if (file.id) {
      const signedUrl = await getSignedUrlByFileId(file.id, clerkToken);
      return signedUrl || null;
    }

    // Note: If neither signedUrl nor id is available, preview will not be available.
    // This is intentional - parent must provide signedUrl or id for secure file access.
    return null;
  };

  const handlePreviewFile = async (file: FileBasic) => {
    if (onPreviewFile) {
      onPreviewFile(file);
      return;
    }
    const signedUrl = await resolveSignedUrl(file);
    if (signedUrl) {
      setPreviewFile({
        fileName: file.fileName,
        signedURL: signedUrl,
      });
      previewModalVisibility.show();
    }
  };

  const handleDownloadFile = async (file: FileBasic) => {
    const signedUrl = await resolveSignedUrl(file);
    if (signedUrl && file.fileName) {
      downloadFileFromLink(signedUrl, file.fileName);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div
          className={cn(
            'flex items-center gap-lg p-xl rounded-xl border-dashed box-border cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#84807a]',
            _className,
            isDragActive
              ? 'border border-border-primary'
              : 'border border-border-primary',
            disabled
              ? 'bg-bg-disabled-subtle border-border-disabled cursor-not-allowed select-none'
              : 'bg-bg-primary border-border-primary hover:border-opacity-100 transition-all duration-100'
          )}
          {...getRootProps()}
        >
          {isUploadingFiles ? (
            <div className="flex items-center justify-between h-full w-full">
              <span className="txt-small-medium text-text-secondary">
                {`Uploading ${uploadingFilesCount} file${uploadingFilesCount > 1 ? 's' : ''}...`}
              </span>
              <div className="flex items-center justify-center p-1">
                <LoadingIndicator />
              </div>
            </div>
          ) : (
            <>
              <input {...getInputProps()} />
              <FeaturedIcon
                icon={<Icons.Upload01 />}
                size="md"
                variant="gray-modern"
              />
              <div className="flex flex-col gap-xs">
                <div className="flex items-center gap-xs">
                  <span className="txt-small-medium text-button-link-text underline underline-offset-2">
                    Click to upload
                  </span>
                  <span className="txt-small-regular text-text-secondary">
                    or drag and drop
                  </span>
                </div>

                {message && (
                  <span className="txt-extra-small-regular text-text-tertiary">
                    {message}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {files?.length ? (
          <div className="flex flex-col gap-lg">
            {files.map((file, idx) => {
              const getFileIcon = (fileName: string) => {
                const extension = Object.keys(fileIconMap).find((ext) =>
                  fileName.endsWith(ext)
                );
                return extension ? fileIconMap[extension] : FileIcon;
              };

              const FileIconComponent: React.ElementType = getFileIcon(
                file.fileName || ''
              );
              return (
                <div
                  key={file.id ?? idx}
                  className={cn(
                    'flex justify-between items-center gap-xs p-4 border border-border-primary bg-bg-primary rounded-xl',
                    _className
                  )}
                >
                  <div className="flex items-center gap-lg flex-1 min-w-0">
                    <FileIconComponent className="h-10 w-10 shrink-0" />
                    <span className="txt-small-medium text-text-secondary truncate">
                      {file.fileName}
                    </span>
                  </div>
                  <div className="flex items-center gap-lg shrink-0">
                    <Icons.Eye
                      onClick={() => handlePreviewFile(file)}
                      className="h-4 w-4 cursor-pointer text-fg-quaternary hover:text-fg-secondary transition-colors"
                    />
                    <Icons.Download01
                      onClick={() => handleDownloadFile(file)}
                      className="h-4 w-4 cursor-pointer text-fg-quaternary hover:text-fg-secondary transition-colors"
                    />
                    <Icons.Trash01
                      onClick={() => handleRemoveFile(idx)}
                      className="h-4 w-4 cursor-pointer text-fg-quaternary hover:text-fg-error transition-colors"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {previewModalVisibility.visible && previewFile && (
        <PreviewDownloadDialog
          isOpen={previewModalVisibility.visible}
          onClose={previewModalVisibility.hide}
          previewFile={previewFile}
        />
      )}
    </>
  );
};

export { MultiFileUploader };
